import { Stack, Typography } from '@mui/material'
import { SmartBrowserDocumentViewer } from './visualization/2d/SmartBrowserDocumentViewer'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import SmartThreejs from './visualization/3d/SmartThreejs'
import { useEffect, useMemo, useState } from 'react'
import { useFullScreen } from './visualization/useFullScreen'
import SensitiveBoundary from '../../vision/SensitiveBoundary'
// Extension lists + viewability helpers live in the import-free leaf module so light
// consumers can use them without pulling this module's heavy viewers (three.js). They
// are re-exported below for backwards compatibility.
import { extensionConversionMap, SmartBrowserDocumentViewerExtensions, CompatibleModelsExtensions, checkDocumentExtension, isExtensionViewable } from './documentExtensions'

// Historia 148: `contextQuery` y `onLoadError` son ADITIVOS y opcionales. Sin ellos el visor se
// comporta exactamente igual que antes (todos los call sites de MIC oficina los omiten).
//   · `contextQuery` — los 4 query params del contexto MOS que el gate de Sun necesita en N2. Su
//     AUSENCIA es lo que mantiene intacto el camino de oficina (S44/D2), asi que aqui por defecto
//     es cadena vacia y no se concatena nada.
//   · `onLoadError` — deja que el consumidor decida el aviso: un documento borrado desde oficina
//     (404) no es lo mismo que un fallo de red (S67).
// Historia 170 (S70/S71): `previewUrl` es una TERCERA prop aditiva y opcional — la URL completa del
// endpoint de previsualización a usar EN VEZ de `document/file/(download|downloadConverted)/...`
// (el repositorio documental construye la suya, `documentRepository/documents/{topic}/{referenceId}/
// {documentId}/preview[?targetExtension=]`, vía `documentRepositoryApi.js`; el propio `convertedExtension`
// que decide GLB para STEP/STP/DXF lo calcula el llamante con el mismo `extensionConversionMap` de
// abajo). Su AUSENCIA (todo call site del módulo la omite) deja `url` exactamente como antes (S71).
const SmartDocumentViewerContent = (props) => {
  const { documentUUID, documentName, tempUUID, extension, requestFullScreen, contextQuery = '', onLoadError, previewUrl = '' } = props
  const { t_ } = useSmartTranslation()
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const fullScreenContainerRef = useFullScreen(requestFullScreen)

  const convertedExtension = useMemo(() => {
    const lowerCaseExtension = extension.toLowerCase()
    if (lowerCaseExtension in extensionConversionMap) {
      return extensionConversionMap[lowerCaseExtension]
    }
    return null
  }, [extension])

  const url = useMemo(() => {
    if (previewUrl) return previewUrl
    const basePath = !convertedExtension
      ? (tempUUID ? 'document/file/download/' + documentUUID + '/' + tempUUID : 'document/file/download/' + documentUUID)
      : (tempUUID ? 'document/file/downloadConverted/' + convertedExtension + '/' + documentUUID + '/' + tempUUID : 'document/file/downloadConverted/' + convertedExtension + '/' + documentUUID)
    return basePath + contextQuery
  }, [documentUUID, tempUUID, convertedExtension, contextQuery, previewUrl])

  useEffect(() => {
    setIsPreviewLoading(true)
  }, [url])

  if (checkDocumentExtension(extension, SmartBrowserDocumentViewerExtensions, convertedExtension)) {
    return (
      <SmartBrowserDocumentViewer
        url={url}
        extension={extension}
        convertedExtension={convertedExtension}
        documentName={documentName}
        requestFullScreen={requestFullScreen}
        onLoadError={onLoadError}
      />
    )
  }

  if (checkDocumentExtension(extension, CompatibleModelsExtensions, convertedExtension)) {
    const modelType = convertedExtension ? convertedExtension.toUpperCase() : extension.toUpperCase()
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }} ref={fullScreenContainerRef}>
        <SmartThreejs
          modelUrl={url}
          modelType={modelType}
          isPreviewLoading={isPreviewLoading}
          setIsPreviewLoading={setIsPreviewLoading}
        />
      </div>
    )
  }
  return (
    <Stack>
      <Typography>{t_('No view available for this file extension: ') + extension} </Typography>
      <Typography>{documentName}</Typography>
    </Stack>
  )
}

// Historia 213 v2 (S189, H-v2-19): `sensitive` es ADITIVA y opcional (por defecto false: sin ella el visor se pinta
// como siempre). Con `true` TODO lo que pinta va dentro de una frontera sensible: al taparse en SENSITIVE_ONLY se
// desmonta el visor con su blob y al destaparse vuelve a pedir el fichero.
const SmartDocumentViewer = ({ sensitive = false, ...viewerProps }) => (
  <SensitiveBoundary sensitive={sensitive}>
    <SmartDocumentViewerContent {...viewerProps} />
  </SensitiveBoundary>
)

export { SmartDocumentViewer, isExtensionViewable, checkDocumentExtension }
